// AppCustom3DSFiservCard;
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiservCardBrand } from './fiservTokenUtils';
import {
  capitalizeWordsInSentence,
  CardType,
  FiservCardDetails,
  ThreeDSAuthData,
} from '@dill/dill-shared';
import { Spinner, X } from 'phosphor-react';
import { BASE_API_URL, CARD_CONNECT_BASE_URL } from '../../../utils/constants';
import AmexCard from '../../../assets/images/amex-card.png';
import VisaCard from '../../../assets/images/visa-card.png';
import DiscoverCard from '../../../assets/images/discover-card.png';
import MastercardCard from '../../../assets/images/mastercard-card.png';
import DinersCard from '../../../assets/images/diners-card.png';
import JCBCard from '../../../assets/images/jcb-card.png';
import axios from 'axios';
import { useAppDispatch } from '../../../redux/store';
import { getFiservCardDetails } from '../../../redux/services/paymentsService';

export type FiservCardResult =
  | { success: true; data: FiservCardDetails }
  | { success: false; error: string };

export interface AppFiservCardInputProps {
  isWarnCardToBeSaved?: boolean;
  onResult?: (result: FiservCardResult) => void;
  amount: string;
  currency?: string;
  supplierId: string;
  orderId?: string;
  isAmexDirect?: boolean;
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    locality?: string;
    administrativeArea?: string;
    postalCode?: string;
    country?: string;
    email?: string;
    phoneNumber?: string;
  };
}

// Card type mapping for Cybersource
const CARD_TYPE_MAP: Record<FiservCardBrand, string> = {
  VISA: '001',
  MASTERCARD: '002',
  AMEX: '003',
  DISCOVER: '004',
  DINERS: '005',
  JCB: '007',
  UNKNOWN: '001',
};

function AppCustom3DSFiservCardInput({
  onResult,
  isWarnCardToBeSaved = false,
  amount,
  currency = 'USD',
  orderId,
  isAmexDirect = false,
  billingAddress,
  supplierId,
}: AppFiservCardInputProps) {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isTokenizingInProgress, setIsTokenizingInProgress] = useState(false);
  const [isCardReadyForPayments, setIsCardReadyForPayments] = useState(false);
  const [fiservCardData, setFiservCardData] = useState<FiservCardDetails | null>(null);
  const [zipcode, setZipcode] = useState<string>('');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [expiryMonth, setExpiryMonth] = useState<string>('');
  const [expiryYear, setExpiryYear] = useState<string>('');
  const [cvv, setCvv] = useState<string>('');
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [expiryError, setExpiryError] = useState<string | null>(null);
  const [cvvError, setCvvError] = useState<string | null>(null);

  // 3DS State
  const [is3DSInProgress, setIs3DSInProgress] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeUrl, setChallengeUrl] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [referenceId, setReferenceId] = useState<string>('');
  const [transactionId, setTransactionId] = useState<string>('');
  const [challengeTimeout, setChallengeTimeout] = useState<number>(300); // 5 minutes
  const [ddcCompleted, setDdcCompleted] = useState(false);
  const challengeIframeRef = useRef<HTMLIFrameElement | null>(null);
  const ddcIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [showDDCIframe, setShowDDCIframe] = useState(false);
  const [challengeFormData, setChallengeFormData] = useState<{
    accessToken: string;
    referenceId: string;
  } | null>(null);

  // Map card brands to their respective images
  const getCardBrandImage = (brand: FiservCardBrand): string | null => {
    switch (brand) {
      case 'AMEX':
        return AmexCard;
      case 'VISA':
        return VisaCard;
      case 'MASTERCARD':
        return MastercardCard;
      case 'DISCOVER':
        return DiscoverCard;
      case 'DINERS':
        return DinersCard;
      case 'JCB':
        return JCBCard;
      default:
        return null;
    }
  };

  // Validate US ZIP code format (5 digits or 5+4 digits)
  const validateZipCode = (zip: string): boolean => {
    if (!zip) {
      setValidationError('ZIP code is required');
      return false;
    }

    // Allow 5-digit format (12345) or ZIP+4 format (12345-6789)
    const zipRegex = /^\d{5}(-\d{4})?$/;

    if (!zipRegex.test(zip)) {
      setValidationError('Invalid ZIP code format. Use 5 digits (12345) or ZIP+4 (12345-6789)');
      return false;
    }

    setValidationError(null);
    return true;
  };

  // Validate card number using Luhn algorithm
  const validateCardNumber = (number: string): boolean => {
    const cleanedNumber = number.replace(/\s/g, '');

    if (!cleanedNumber) {
      setCardNumberError('Card number is required');
      return false;
    }

    if (!/^\d{13,19}$/.test(cleanedNumber)) {
      setCardNumberError('Card number must be between 13 and 19 digits');
      return false;
    }

    // Luhn algorithm
    let sum = 0;
    let isEven = false;
    for (let i = cleanedNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanedNumber[i], 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }

    if (sum % 10 !== 0) {
      setCardNumberError('Invalid card number');
      return false;
    }

    setCardNumberError(null);
    return true;
  };

  // Validate expiry date
  const validateExpiry = (month: string, year: string): boolean => {
    if (!month || !year) {
      setExpiryError('Expiry date is required');
      return false;
    }

    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year.length === 2 ? `20${year}` : year, 10);

    if (monthNum < 1 || monthNum > 12) {
      setExpiryError('Invalid month');
      return false;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
      setExpiryError('Card has expired');
      return false;
    }

    setExpiryError(null);
    return true;
  };

  // Validate CVV
  const validateCVV = (cvvValue: string, cardBrand?: FiservCardBrand): boolean => {
    if (!cvvValue) {
      setCvvError('CVV is required');
      return false;
    }

    const expectedLength = cardBrand === 'AMEX' ? 4 : 3;
    const cvvRegex = new RegExp(`^\\d{${expectedLength}}$`);

    if (!cvvRegex.test(cvvValue)) {
      setCvvError(`CVV must be ${expectedLength} digits`);
      return false;
    }

    setCvvError(null);
    return true;
  };

  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  // Mask card number for display during 3DS (show only last 4 digits)
  const maskCardNumber = (number: string): string => {
    const cleaned = number.replace(/\s/g, '');
    if (cleaned.length < 4) return number;
    const last4 = cleaned.slice(-4);
    const maskedLength = cleaned.length - 4;
    const maskedGroups = Math.ceil(maskedLength / 4);
    let masked = '';
    for (let i = 0; i < maskedGroups; i++) {
      masked += '**** ';
    }
    return masked + last4;
  };

  // Detect card brand from number
  const detectCardBrand = (number: string): FiservCardBrand => {
    const cleaned = number.replace(/\s/g, '');

    if (/^4/.test(cleaned)) return 'VISA';
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return 'MASTERCARD';
    if (/^3[47]/.test(cleaned)) return 'AMEX';
    if (/^6(?:011|5)/.test(cleaned)) return 'DISCOVER';
    if (/^3(?:0[0-5]|[68])/.test(cleaned)) return 'DINERS';
    if (/^(?:2131|1800|35\d{3})/.test(cleaned)) return 'JCB';

    return 'UNKNOWN';
  };

  // Tokenize card number using CardConnect
  const tokenizeCard = async (cardNum: string): Promise<string | null> => {
    try {
      const response = await axios.post(`${CARD_CONNECT_BASE_URL}/cardsecure/api/v1/ccn/tokenize`, {
        account: cardNum,
      });

      if (response.status === 200 && response.data?.token) {
        return response.data.token;
      }

      console.error('Tokenization failed: No token in response', response.data);
      return null;
    } catch (err: any) {
      console.error('Card tokenization error:', err);
      return null;
    }
  };

  // Handle 3DS payment after successful authentication
  const handleTokeniseAndFinalize3DS = async ({
    threeDSData,
    is3DSEnabled,
  }: {
    threeDSData?: ThreeDSAuthData | null;
    is3DSEnabled: boolean;
  }) => {
    const isCardValid = validateCardNumber(cardNumber);
    const isExpiryValid = validateExpiry(expiryMonth, expiryYear);
    const cardBrand = detectCardBrand(cardNumber);
    const isCvvValid = validateCVV(cvv, cardBrand);
    const isZipValid = validateZipCode(zipcode);

    if (!isCardValid || !isExpiryValid || !isCvvValid || !isZipValid) {
      return;
    }
    if (!threeDSData && is3DSEnabled) {
      const errorMessage = '3DS authentication data not available';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return;
    }
    if (!is3DSEnabled) {
      const errorMessage = '3DS authentication data not available';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return;
    }

    // Validate required 3DS fields
    if (threeDSData && is3DSEnabled && (!threeDSData?.eci || !threeDSData?.cavv)) {
      const errorMessage = '3DS data incomplete: eci and cavv are required for payment';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return;
    }

    setIsTokenizingInProgress(true);

    try {
      // Tokenize the card first
      const cleanedCardNumber = cardNumber.replace(/\s/g, '');
      const tokenizedCard = await tokenizeCard(cleanedCardNumber);

      if (!tokenizedCard) {
        setError('Failed to tokenize card. Please try again.');
        onResult?.({
          success: false,
          error: 'Card tokenization failed',
        });
        setIsTokenizingInProgress(false);
        return;
      }
      const cardDetailsResponse = await dispatch(
        getFiservCardDetails({ fiservCardToken: tokenizedCard })
      );
      setIsValidating(false);
      let cardTypeFetched: CardType = 'UNKNOWN';

      if (
        cardDetailsResponse.type === 'payments/getFiservCardDetails/fulfilled' &&
        cardDetailsResponse.payload
      ) {
        const payload: any = cardDetailsResponse.payload;
        if (payload && payload.data) {
          cardTypeFetched = payload.data.cardType;
        }
      }
      if (
        !cardDetailsResponse.payload ||
        cardDetailsResponse.type === 'payments/getFiservCardDetails/rejected'
      ) {
        let errorMsg = 'Error validating card. Failed to get card details ';
        if (cardDetailsResponse?.payload) {
          errorMsg = `Error validating card. ${cardDetailsResponse?.payload}`;
        }
        setError(errorMsg);
        setIsValidating(false);
        setFiservCardData(null);
        setZipcode('');
        onResult?.({ success: false, error: errorMsg });
        return;
      } // Create card data
      // const cleanedCardNumber = cardNumber.replace(/\s/g, '');

      const cardData: FiservCardDetails = {
        token: tokenizedCard,
        brand: cardBrand,
        last4: cleanedCardNumber.slice(-4),
        expiry: `${expiryMonth}/${expiryYear}`,
        zipCode: zipcode,

        cardType: cardTypeFetched,
        cardName: `${capitalizeWordsInSentence(
          cardBrand.toLowerCase()
        )} ****${cleanedCardNumber.slice(-4)}`,
      };
      setIsCardReadyForPayments(true);
      onResult?.({
        success: true,
        data: {
          ...cardData,
          ...(threeDSData && is3DSEnabled
            ? {
                threeDSData: {
                  eci: threeDSData.eci,
                  cavv: threeDSData.cavv,
                  xid: threeDSData.xid,
                  directoryServerTransactionId: threeDSData.directoryServerTransactionId,
                  paresStatus: threeDSData.paresStatus,
                  authenticationStatusMsg: threeDSData.authenticationStatusMsg,
                },
              }
            : {}),
        },
      });
    } catch (err: any) {
      setIsCardReadyForPayments(false);
      console.error('Payment error:', err);
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Payment processing failed';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
    } finally {
      setIsTokenizingInProgress(false);
    }
  };

  // 3DS Setup - Call backend to get JWT and referenceId
  const setup3DS = async (
    cardNum: string,
    expMonth: string,
    expYear: string,
    cardBrand: FiservCardBrand
  ): Promise<{
    accessToken: string;
    referenceId: string;
    deviceDataCollectionUrl: string;
  } | null> => {
    try {
      const cardType = CARD_TYPE_MAP[cardBrand] || '001';
      const response = await axios.post(`${BASE_API_URL}/generics/fiserv3ds/setup`, {
        supplierId,
        cardType,
        expirationMonth: expMonth,
        expirationYear: expYear,
        cardNumber: cardNum,
      });
      // console.log('setup3DS', response.data);

      const { consumerAuthenticationInformation } = response.data;
      return {
        accessToken: consumerAuthenticationInformation.accessToken,
        referenceId: consumerAuthenticationInformation.referenceId,
        deviceDataCollectionUrl: consumerAuthenticationInformation.deviceDataCollectionUrl,
      };
    } catch (err: any) {
      console.error('3DS Setup error:', err);
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Failed to setup 3DS authentication';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return null;
    }
  };

  // Device Data Collection - Collect device fingerprint
  const collectDeviceData = useCallback(
    (token: string, refId: string, ddcUrl: string): Promise<boolean> => {
      return new Promise((resolve) => {
        setShowDDCIframe(true);

        // Listen for the DDC completion message
        const handleDDCMessage = (event: MessageEvent) => {
          if (event.data && event.data === 'profile.completed') {
            setDdcCompleted(true);
            setShowDDCIframe(false);
            window.removeEventListener('message', handleDDCMessage);
            resolve(true);
          }
        };

        window.addEventListener('message', handleDDCMessage);

        // Set timeout for DDC (30 seconds)
        setTimeout(() => {
          if (!ddcCompleted) {
            setShowDDCIframe(false);
            window.removeEventListener('message', handleDDCMessage);
            resolve(true); // Continue even if DDC times out
          }
        }, 30000);

        // Create and submit DDC form
        const ddcForm = document.createElement('form');
        ddcForm.id = 'ddc-form';
        ddcForm.method = 'POST';
        ddcForm.action = ddcUrl;
        ddcForm.target = 'ddc-iframe';

        const jwtInput = document.createElement('input');
        jwtInput.type = 'hidden';
        jwtInput.name = 'JWT';
        jwtInput.value = token;
        ddcForm.appendChild(jwtInput);

        const refIdInput = document.createElement('input');
        refIdInput.type = 'hidden';
        refIdInput.name = 'ReferenceId';
        refIdInput.value = refId;
        ddcForm.appendChild(refIdInput);

        document.body.appendChild(ddcForm);

        // Wait for iframe to load then submit
        setTimeout(() => {
          ddcForm.submit();
          document.body.removeChild(ddcForm);
        }, 100);
      });
    },
    [ddcCompleted]
  );

  const handleAuthResponse = ({
    veresEnrolled,
    paresStatus,
    authData,
  }: {
    veresEnrolled: string;
    paresStatus: string;
    authData?: ThreeDSAuthData | null;
  }) => {
    console.log({ veresEnrolled, paresStatus });
    // =====================================================
    // ENROLLMENT RESULT INTERPRETATIONS (CyberSource Docs)
    // =====================================================
    // These if statements describe each enrollment result case
    // based on VERes (Verification Response) and PARes (Payer Authentication Response)
    // Reference: https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-enroll-intro.html

    // Case 1: PENDING_AUTHENTICATION - Challenge Required
    // VERes enrolled = Y, PARes status = C
    // The account number is enrolled in payer authentication.
    // Cardholder must be challenged to authenticate before authorizing the transaction.
    if (veresEnrolled === 'Y' && paresStatus === 'C') {
      console.log('3DS Case: PENDING_AUTHENTICATION - Card enrolled, challenge required');
      // handleTokeniseAndFinalize3DS({ threeDSData: authData, is3DSEnabled: true });
      // return;
    }

    // Case 2: AUTHENTICATION_SUCCESSFUL - Frictionless Success
    // VERes enrolled = Y, PARes status = Y
    // Account is enrolled and cardholder was successfully authenticated without challenge.
    // Payer authentication data must be included in authorization for liability shift.
    if (veresEnrolled === 'Y' && paresStatus === 'Y') {
      console.log('3DS Case: AUTHENTICATION_SUCCESSFUL - Frictionless authentication passed');
      handleTokeniseAndFinalize3DS({ threeDSData: authData, is3DSEnabled: true });
      setIs3DSInProgress(false);
      return true;
    }

    // Case 3: Attempt Stand-in Frictionless Authentication
    // VERes enrolled = Y, PARes status = A
    // Account is enrolled but issuer does not support the program (stand-in authentication).
    // Blocked for maximum fraud protection — issuer could not fully authenticate.
    if (veresEnrolled === 'Y' && paresStatus === 'A') {
      console.log('3DS Case: STAND-IN AUTHENTICATION - Issuer does not support program');
      handleTokeniseAndFinalize3DS({ threeDSData: authData, is3DSEnabled: true });
      setIs3DSInProgress(false);

      // console.log({ authData });
      // // Un comment this to disable. But its advisabe to enable this.
      // This means you're also rejecting transactions where the card network would have taken fraud liability on your behalf.
      // console.log('3DS Case: STAND-IN AUTHENTICATION - Blocking for fraud protection');
      // const errorMessage =
      //   'Your card could not be fully authenticated. Please try a different payment method.';
      // setError(errorMessage);
      // onResult?.({
      //   success: false,
      //   error: errorMessage,
      // });

      return true;
    }

    // Case 4: Card Not Enrolled
    // VERes enrolled = B or U
    // Account is not eligible for payer authentication program, authentication was bypassed,
    // or an error/timeout occurred. Can request authorization but NO liability shift protection.
    if (veresEnrolled === 'B' || veresEnrolled === 'U') {
      console.log('3DS Case: NOT ENROLLED - Card not eligible, blocking for fraud protection');
      const errorMessage =
        'This card is not eligible for 3D Secure authentication. Please try a different payment method.';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return true;
    }

    // Case 5: Unavailable Frictionless Authentication
    // VERes enrolled = Y, PARes status = U
    // Account is enrolled but authentication is currently unavailable.
    // Can retry authentication or proceed with authorization but NO liability shift.
    // Merchant remains liable for any chargeback without customer authentication.
    if (veresEnrolled === 'Y' && paresStatus === 'U') {
      console.log(
        '3DS Case: AUTHENTICATION UNAVAILABLE - Enrolled but service unavailable blocking for fraud protection'
      );
      const errorMessage =
        '3D Secure authentication is currently unavailable. Please try again later or use a different payment method.';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return true;
    }

    // Case 6: Failed Frictionless Authentication
    // VERes enrolled = Y, PARes status = N
    // Account is enrolled but frictionless authentication FAILED.
    // CANNOT submit transaction for authorization. Ask for another form of payment.
    if (veresEnrolled === 'Y' && paresStatus === 'N') {
      console.log('3DS Case: AUTHENTICATION FAILED - Frictionless auth failed, do not authorize');
      const errorMessage =
        '3D Secure verification failed for your card. Please try a different payment method.';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return true;
    }

    // Case 7: Rejected Frictionless Authentication
    // VERes enrolled = Y, PARes status = R
    // Account is enrolled but frictionless authentication was REJECTED by issuing bank
    // without requiring a challenge. CANNOT submit for authorization.
    // Ask for another form of payment.
    if (veresEnrolled === 'Y' && paresStatus === 'R') {
      console.log('3DS Case: AUTHENTICATION REJECTED - Issuer rejected without challenge');
      const errorMessage =
        'Your card was declined by the issuing bank during 3D Secure authentication. Please try a different payment method.';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return true;
    }

    return false;
  };

  // Check Enrollment - Call backend to check if card is enrolled
  const checkEnrollment = async ({
    token,
    refId,
    cardDetails,
  }: {
    token: string;
    refId: string;
    cardDetails: {
      cardType: string;
      expirationMonth: string;
      expirationYear: string;
      cardNumber: string;
    };
  }): Promise<any> => {
    try {
      const response = await axios.post(`${BASE_API_URL}/generics/fiserv3ds/checkEnrollment`, {
        supplierId,
        referenceId: refId,
        token: token,
        amount,
        currency,
        orderId,
        billingAddress,
        cardDetails,
        deviceData: {
          userAgent: navigator.userAgent,
          acceptHeader: 'application/json',
        },
      });
      // console.log('checkEnrollment', response.data);

      return response.data;
    } catch (err: any) {
      console.error('Check Enrollment error:', err);
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Failed to check card enrollment';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return null;
    }
  };

  // Validate Authentication - Get final authentication results
  const validateAuthentication = async (
    txnId: string,
    reqId: string,
    cardDetails: {
      cardType: string;
      expirationMonth: string;
      expirationYear: string;
      cardNumber: string;
    }
  ): Promise<any> => {
    try {
      const response = await axios.post(`${BASE_API_URL}/generics/fiserv3ds/validate`, {
        supplierId,
        transactionId: txnId,
        requestId: reqId,
        amount,
        currency,
        cardDetails,
      });

      // console.log('validateAuthentication', response.data);

      return response.data;
    } catch (err: any) {
      console.error('Validate Authentication error:', err);
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Failed to validate authentication';
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return null;
    }
  };

  // Handle challenge completion
  const handleChallengeComplete = useCallback(async () => {
    setShowChallengeModal(false);
    setIs3DSInProgress(true);

    // Get card details from state for validation
    const cleanedCardNumber = cardNumber.replace(/\s/g, '');
    const cardBrand = detectCardBrand(cardNumber);
    const cardType = CARD_TYPE_MAP[cardBrand] || '001';

    const validateAuthenticationResult = await validateAuthentication(transactionId, referenceId, {
      cardType,
      expirationMonth: expiryMonth,
      expirationYear: expiryYear,
      cardNumber: cleanedCardNumber,
    });
    const consumerAuthenticationInformation =
      validateAuthenticationResult?.consumerAuthenticationInformation;
    const veresEnrolled = consumerAuthenticationInformation?.veresEnrolled;
    const paresStatus = consumerAuthenticationInformation?.paresStatus;
    const eci = consumerAuthenticationInformation?.eci || consumerAuthenticationInformation?.eciRaw;
    const cavv =
      consumerAuthenticationInformation?.cavv ||
      consumerAuthenticationInformation?.ucafAuthenticationData;

    const authData: ThreeDSAuthData | null =
      eci && cavv
        ? {
            authenticationTransactionId:
              consumerAuthenticationInformation?.authenticationTransactionId ||
              validateAuthenticationResult?.id,
            eci: eci,
            eciRaw: consumerAuthenticationInformation?.eciRaw,
            cavv: cavv,
            xid: consumerAuthenticationInformation?.xid,
            paresStatus: consumerAuthenticationInformation?.paresStatus,
            authenticationStatusMsg: consumerAuthenticationInformation?.authenticationStatusMsg,
            directoryServerTransactionId:
              consumerAuthenticationInformation?.directoryServerTransactionId,
          }
        : null;
    if (
      validateAuthenticationResult.status === 'AUTHENTICATION_FAILED' &&
      consumerAuthenticationInformation?.cardholderMessage
    ) {
      const errorMessage = consumerAuthenticationInformation?.cardholderMessage;
      console.log({ errorMessage });
      console.log({ paresStatus, veresEnrolled });
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return;
    }
    if (
      validateAuthenticationResult.status === 'AUTHENTICATION_FAILED' &&
      validateAuthenticationResult?.errorInformation?.message
    ) {
      console.log({ paresStatus, veresEnrolled });
      const errorMessage = validateAuthenticationResult?.errorInformation?.message;
      console.log({ errorMessage });
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      return;
    }

    const handleAuthResponseRes = handleAuthResponse({ paresStatus, veresEnrolled, authData });
    if (handleAuthResponseRes) {
      return;
    }

    if (
      authData &&
      (validateAuthenticationResult.status === 'SUCCESS' ||
        validateAuthenticationResult.status === 'AUTHENTICATION_SUCCESSFUL' ||
        validateAuthenticationResult.status === 'ENROLLED')
    ) {
      // setThreeDSData(authData);

      // Combine card data with 3DS data
      if (fiservCardData) {
        handleTokeniseAndFinalize3DS({ threeDSData: authData, is3DSEnabled: true });
      }
    } else if (
      !authData &&
      (validateAuthenticationResult.status === 'SUCCESS' ||
        validateAuthenticationResult.status === 'AUTHENTICATION_SUCCESSFUL' ||
        validateAuthenticationResult.status === 'ENROLLED')
    ) {
      if (fiservCardData) {
        handleTokeniseAndFinalize3DS({ is3DSEnabled: false });
      }
    } else if (validateAuthenticationResult?.status === 'AUTHENTICATION_FAILED') {
      console.log({ paresStatus, veresEnrolled });
      // Handle authentication failure with error message from response
      const errorMessage =
        validateAuthenticationResult.errorInformation?.message ||
        '3DS authentication failed. Please try again.';
      console.log({ errorMessage });
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
    } else {
      setError('3DS authentication failed. Please try again.');
      onResult?.({
        success: false,
        error: '3DS authentication failed',
      });
    }

    setIs3DSInProgress(false);
  }, [transactionId, referenceId, fiservCardData, onResult, cardNumber, expiryMonth, expiryYear]);
  useEffect(() => {
    onResult?.({
      success: false,
      error: '',
    });
    setIsCardReadyForPayments(false);

    return () => {
      return;
    };
  }, [cardNumber, expiryMonth, expiryYear, zipcode, cvv]);

  // Challenge Modal Countdown Timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showChallengeModal && challengeTimeout > 0) {
      timer = setInterval(() => {
        setChallengeTimeout((prev) => {
          if (prev <= 1) {
            setShowChallengeModal(false);
            const errorMessage = '3DS authentication timed out. Please try again.';
            setError(errorMessage);
            onResult?.({
              success: false,
              error: errorMessage,
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [showChallengeModal, challengeTimeout, onResult]);

  // Submit challenge form when modal opens
  useEffect(() => {
    if (showChallengeModal && challengeUrl && challengeFormData) {
      // Create and submit form to POST to ACS URL
      const form = document.createElement('form');
      form.id = 'challenge-form';
      form.method = 'POST';
      form.action = challengeUrl;
      form.target = 'challenge-iframe';

      // Add JWT token
      const jwtInput = document.createElement('input');
      jwtInput.type = 'hidden';
      jwtInput.name = 'JWT';
      jwtInput.value = challengeFormData.accessToken;
      form.appendChild(jwtInput);

      // Add reference ID
      const refIdInput = document.createElement('input');
      refIdInput.type = 'hidden';
      refIdInput.name = 'ReferenceId';
      refIdInput.value = challengeFormData.referenceId;
      form.appendChild(refIdInput);

      document.body.appendChild(form);

      // Wait for iframe to be ready then submit
      setTimeout(() => {
        form.submit();
        document.body.removeChild(form);
      }, 100);
    }
  }, [showChallengeModal, challengeUrl, challengeFormData]);

  // Listen for challenge completion messages
  useEffect(() => {
    const handleChallengeMessage = (event: MessageEvent) => {
      // Handle various challenge completion message types
      if (
        // event.data === '3ds-callback' ||
        // event.data?.type === '3ds-callback' ||
        // event.data?.type === 'TransactionId' ||
        event.data?.type === 'challenge.complete' &&
        !is3DSInProgress
      ) {
        console.log('3DS challenge complete detected');
        handleChallengeComplete();
      }
    };

    window.addEventListener('message', handleChallengeMessage);
    return () => window.removeEventListener('message', handleChallengeMessage);
  }, [handleChallengeComplete]);

  // Main 3DS Authentication Flow
  const perform3DSAuthentication = async (
    cardNum: string,
    expMonth: string,
    expYear: string,
    cardBrand: FiservCardBrand
  ) => {
    setIs3DSInProgress(true);
    setError(null);

    // Step 1: Setup 3DS
    const setupResult = await setup3DS(cardNum, expMonth, expYear, cardBrand);
    if (!setupResult) {
      setIs3DSInProgress(false);
      return;
    }

    setAccessToken(setupResult.accessToken);
    setReferenceId(setupResult.referenceId);

    // Step 2: Device Data Collection (if DDC URL is provided)
    // const ddcUrl = 'https://centinelapistagetest.cardinalcommercesession.com/V1/Cruise/Collect';
    const ddcUrl = setupResult.deviceDataCollectionUrl;
    await collectDeviceData(setupResult.accessToken, setupResult.referenceId, ddcUrl);

    // Step 3: Check Enrollment

    const cardType = CARD_TYPE_MAP[cardBrand] || '001';
    const enrollmentResult = await checkEnrollment({
      token: setupResult.accessToken,
      refId: setupResult.referenceId,
      cardDetails: {
        cardType,
        expirationMonth: expMonth,
        expirationYear: expYear,
        cardNumber: cardNum,
      },
    });

    if (!enrollmentResult) {
      setIs3DSInProgress(false);
      return;
    }
    const consumerAuthenticationInformation = enrollmentResult?.consumerAuthenticationInformation;
    // console.log({ consumerAuthenticationInformation });
    const veresEnrolled = consumerAuthenticationInformation?.veresEnrolled;
    const paresStatus = consumerAuthenticationInformation?.paresStatus;
    const eci = consumerAuthenticationInformation?.eci || consumerAuthenticationInformation?.eciRaw;
    const cavv =
      consumerAuthenticationInformation?.cavv ||
      consumerAuthenticationInformation?.ucafAuthenticationData;

    const authData: ThreeDSAuthData | null =
      eci && cavv
        ? {
            authenticationTransactionId:
              consumerAuthenticationInformation?.authenticationTransactionId ||
              enrollmentResult?.id,
            eci: eci,
            eciRaw: consumerAuthenticationInformation?.eciRaw,
            cavv: cavv,
            xid: consumerAuthenticationInformation?.xid,
            paresStatus: consumerAuthenticationInformation?.paresStatus,
            authenticationStatusMsg: consumerAuthenticationInformation?.authenticationStatusMsg,
            directoryServerTransactionId:
              consumerAuthenticationInformation?.directoryServerTransactionId,
          }
        : null;
    if (
      enrollmentResult.status === 'AUTHENTICATION_FAILED' &&
      consumerAuthenticationInformation?.cardholderMessage
    ) {
      console.log({ paresStatus, veresEnrolled });
      const errorMessage = consumerAuthenticationInformation?.cardholderMessage;
      console.log({ errorMessage });
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return;
    }
    if (
      enrollmentResult.status === 'AUTHENTICATION_FAILED' &&
      enrollmentResult?.errorInformation?.message
    ) {
      console.log({ paresStatus, veresEnrolled });
      const errorMessage = enrollmentResult?.errorInformation?.message;
      console.log({ errorMessage });
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
      return;
    }
    const handleAuthResponseRes = handleAuthResponse({ paresStatus, veresEnrolled, authData });
    if (handleAuthResponseRes) {
      return;
    }

    if (
      enrollmentResult.status === 'AUTHENTICATION_SUCCESSFUL' ||
      enrollmentResult.status === 'SUCCESS'
    ) {
      handleTokeniseAndFinalize3DS({ threeDSData: authData, is3DSEnabled: true });

      setIs3DSInProgress(false);
    } else if (
      enrollmentResult.status === 'PENDING_AUTHENTICATION' ||
      enrollmentResult.status === 'COMPLETED'
    ) {
      // Challenge flow - card is enrolled and requires step-up authentication
      // VERes=Y, PARes=C indicates challenge is required
      if (
        consumerAuthenticationInformation.stepUpUrl &&
        consumerAuthenticationInformation.accessToken
      ) {
        setChallengeUrl(consumerAuthenticationInformation.stepUpUrl);
        setAccessToken(consumerAuthenticationInformation.accessToken);
        setTransactionId(consumerAuthenticationInformation.authenticationTransactionId || '');
        setChallengeFormData({
          accessToken: consumerAuthenticationInformation.accessToken,
          referenceId: setupResult.referenceId,
        });
        setChallengeTimeout(300); // Reset timeout
        setShowChallengeModal(true);
        setIs3DSInProgress(false);
      } else {
        const errorMessage = 'Challenge required but no ACS URL provided';
        setError(errorMessage);
        onResult?.({
          success: false,
          error: errorMessage,
        });
        setIs3DSInProgress(false);
      }
    } else if (
      enrollmentResult.status === 'AUTHENTICATION_FAILED' ||
      enrollmentResult.status === 'FAILED'
    ) {
      // Authentication failed (PARes=N or R) - cardholder failed or rejected
      // Ask user to try a different payment method
      setError('Authentication failed. Please try a different payment method.');
      onResult?.({
        success: false,
        error: '3DS authentication failed',
      });
      setIs3DSInProgress(false);
    } else if (
      enrollmentResult.status === 'NOT_ENROLLED' ||
      enrollmentResult.status === 'ENROLLMENT_STATUS_UNAVAILABLE'
    ) {
      // Card not enrolled (VERes=B or U) or status unavailable (PARes=U)
      // Proceed without 3DS - no liability shift but transaction can continue
      // setThreeDSData({ status: 'NOT_ENROLLED' });
      if (fiservCardData) {
        handleTokeniseAndFinalize3DS({ is3DSEnabled: false });
      }
      setIs3DSInProgress(false);
    } else if (enrollmentResult.stepUpUrl && enrollmentResult.accessToken) {
      // Fallback: Challenge flow detected by presence of stepUpUrl and accessToken
      // even if status is not explicitly PENDING_AUTHENTICATION
      setChallengeUrl(enrollmentResult.stepUpUrl);
      setAccessToken(enrollmentResult.accessToken);
      setTransactionId(enrollmentResult.transactionId || '');
      setChallengeFormData({
        accessToken: enrollmentResult.accessToken,
        referenceId: setupResult.referenceId,
      });
      setChallengeTimeout(300); // Reset timeout
      setShowChallengeModal(true);
      setIs3DSInProgress(false);
    } else {
      // Unknown status - log and show error
      console.error('Unknown 3DS enrollment status:', enrollmentResult);
      const errorMessage = `3DS authentication status: ${enrollmentResult.status}`;
      setError(errorMessage);
      onResult?.({
        success: false,
        error: errorMessage,
      });
      setIs3DSInProgress(false);
    }
  };

  useEffect(() => {
    setFiservCardData(null);
    setZipcode('');
    setCardNumber('');
    setExpiryMonth('');
    setExpiryYear('');
    setCvv('');
    setValidationError('');
    setCardNumberError('');
    setExpiryError('');
    setCvvError('');
    setError('');
    setIsCardReadyForPayments(false);
    // setThreeDSData(null);
    return () => {};
  }, []);

  // Handle form submission and trigger 3DS and make payment after successful auth
  const handleSubmit = async () => {
    // Validate all fields
    const isCardValid = validateCardNumber(cardNumber);
    const isExpiryValid = validateExpiry(expiryMonth, expiryYear);
    const cardBrand = detectCardBrand(cardNumber);
    const isCvvValid = validateCVV(cvv, cardBrand);
    const isZipValid = validateZipCode(zipcode);

    if (!isCardValid || !isExpiryValid || !isCvvValid || !isZipValid) {
      return;
    }

    // Block Amex on 3DS-enabled suppliers unless supplier is Amex Direct
    if (cardBrand === 'AMEX' && !isAmexDirect) {
      setCardNumberError(
        'American Express is not permitted for this transaction. Please try a different card type.'
      );
      return;
    }

    // Create card data
    const cleanedCardNumber = cardNumber.replace(/\s/g, '');
    const cardData: FiservCardDetails = {
      token: '', // Token will be generated by backend if needed
      brand: cardBrand,
      last4: cleanedCardNumber.slice(-4),
      expiry: `${expiryMonth}/${expiryYear}`,
      zipCode: zipcode,
      cardType: 'CREDIT', // Default, can be determined by backend
      cardName: `${capitalizeWordsInSentence(
        cardBrand.toLowerCase()
      )} ****${cleanedCardNumber.slice(-4)}`,
    };

    setFiservCardData(cardData);
    // console.log({ cardData });
    setIsValidating(true);

    // Start 3DS authentication
    await perform3DSAuthentication(cleanedCardNumber, expiryMonth, expiryYear, cardBrand);
    setIsValidating(false);
  };

  const isProcessing = isValidating || is3DSInProgress || isTokenizingInProgress;

  return (
    <div className="flex py-4 px-4 sm:px-6 w-full">
      <form
        name="tokenform"
        id="tokenform"
        className="flex flex-col w-full"
        onSubmit={(e) => e.preventDefault()}>
        <div className="bg-white rounded-lg mb-2 flex h-fit w-full relative flex-col">
          {/* Card Number Input */}
          <div className="mb-3 sm:mb-4">
            <label htmlFor="cardNumber" className="block text-sm font-semibold text-gray-700 mb-1">
              Card Number
            </label>
            <div className="relative">
              <input
                id="cardNumber"
                type="text"
                value={
                  isProcessing || isCardReadyForPayments ? maskCardNumber(cardNumber) : cardNumber
                }
                disabled={isProcessing}
                onChange={(e) => {
                  const formatted = formatCardNumber(e.target.value);
                  if (formatted.replace(/\s/g, '').length <= 19) {
                    setCardNumber(formatted);
                    if (cardNumberError) setCardNumberError(null);
                    // Real-time Amex detection for 3DS-enabled, non-Amex-Direct suppliers
                    const detected = detectCardBrand(formatted);
                    if (detected === 'AMEX' && !isAmexDirect) {
                      setCardNumberError(
                        'American Express is not permitted for this transaction. Please try a different card type.'
                      );
                    }
                  }
                }}
                onBlur={() => validateCardNumber(cardNumber)}
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                data-private="true"
                className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base focus:ring-2 focus:outline-none transition-all pr-16 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  cardNumberError
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
                }`}
              />
              {cardNumber && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {(() => {
                    const brand = detectCardBrand(cardNumber);
                    const brandImage = getCardBrandImage(brand);
                    if (brandImage) {
                      return (
                        <img
                          src={brandImage}
                          alt={`${brand} card`}
                          className="h-6 w-auto object-contain"
                        />
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
            {cardNumberError && <p className="text-sm text-red-500 mt-1">{cardNumberError}</p>}
          </div>

          {/* Expiry and CVV Row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="flex-1">
              <label
                htmlFor="expiryMonth"
                className="block text-sm font-semibold text-gray-700 mb-1">
                Expiry Date
              </label>
              <div className="flex gap-2">
                <input
                  id="expiryMonth"
                  type="text"
                  value={expiryMonth}
                  disabled={isProcessing}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 2) {
                      setExpiryMonth(value);
                      if (expiryError) setExpiryError(null);
                    }
                  }}
                  onBlur={() => validateExpiry(expiryMonth, expiryYear)}
                  placeholder="MM"
                  maxLength={2}
                  data-private="true"
                  className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base focus:ring-2 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    expiryError
                      ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
                  }`}
                />
                <span className="flex items-center text-gray-500">/</span>
                <input
                  id="expiryYear"
                  type="text"
                  value={expiryYear}
                  disabled={isProcessing}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 4) {
                      setExpiryYear(value);
                      if (expiryError) setExpiryError(null);
                    }
                  }}
                  onBlur={() => validateExpiry(expiryMonth, expiryYear)}
                  placeholder="YYYY"
                  maxLength={4}
                  data-private="true"
                  className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base focus:ring-2 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    expiryError
                      ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
                  }`}
                />
              </div>
              {expiryError && <p className="text-sm text-red-500 mt-1">{expiryError}</p>}
            </div>

            <div className="w-full sm:w-24">
              <label htmlFor="cvv" className="block text-sm font-semibold text-gray-700 mb-1">
                CVV
              </label>
              <input
                id="cvv"
                type="text"
                value={isProcessing || isCardReadyForPayments ? '***' : cvv}
                disabled={isProcessing}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  const maxLength = detectCardBrand(cardNumber) === 'AMEX' ? 4 : 3;
                  if (value.length <= maxLength) {
                    setCvv(value);
                    if (cvvError) setCvvError(null);
                  }
                }}
                onBlur={() => validateCVV(cvv, detectCardBrand(cardNumber))}
                placeholder={detectCardBrand(cardNumber) === 'AMEX' ? '1234' : '123'}
                maxLength={detectCardBrand(cardNumber) === 'AMEX' ? 4 : 3}
                data-private="true"
                className={`w-full border rounded-lg px-3 py-2.5 sm:py-2 text-base focus:ring-2 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  cvvError
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
                }`}
              />
              {cvvError && <p className="text-sm text-red-500 mt-1">{cvvError}</p>}
            </div>
          </div>

          {/* ZIP Code Input */}
          <div className="mb-3 sm:mb-4">
            <label htmlFor="zipcode" className="block text-sm font-semibold text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              id="zipcode"
              type="text"
              value={zipcode}
              disabled={isProcessing}
              onChange={(e) => {
                setZipcode(e.target.value);
                if (validationError) setValidationError(null);
              }}
              onBlur={() => validateZipCode(zipcode)}
              placeholder="12345 or 12345-6789"
              maxLength={10}
              className={`w-full sm:w-48 border rounded-lg px-3 py-2.5 sm:py-2 text-base focus:ring-2 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed ${
                validationError && validationError.toLowerCase().includes('zip')
                  ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-100'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100 hover:border-gray-400'
              }`}
            />
            {validationError && validationError.toLowerCase().includes('zip') && (
              <p className="text-sm text-red-500 mt-1">{validationError}</p>
            )}
          </div>

          {/* Submit Button */}
          {!isCardReadyForPayments && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isProcessing}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                isProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              }`}>
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <Spinner className="animate-spin mr-2" size={20} />
                  Processing...
                </span>
              ) : (
                'Validate card'
              )}
            </button>
          )}
        </div>

        {validationError && !validationError.toLowerCase().includes('zip') && (
          <p className="text-sm text-yellow-500">{validationError}</p>
        )}
        {isWarnCardToBeSaved && (
          <p className="text-sm text-yellow-500">Card details will be saved for future payments</p>
        )}

        {error && <p className="text-sm text-red-800">{error}</p>}
      </form>

      {/* Hidden DDC Iframe */}
      {showDDCIframe && (
        <iframe
          id="ddc-iframe"
          name="ddc-iframe"
          ref={ddcIframeRef}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            border: 'none',
            left: '-9999px',
          }}
          title="Device Data Collection"></iframe>
      )}

      {/* Challenge Modal */}
      {showChallengeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Verify Your Card</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Time remaining: {Math.floor(challengeTimeout / 60)}:
                  {(challengeTimeout % 60).toString().padStart(2, '0')}
                </span>
                <button
                  onClick={() => {
                    setShowChallengeModal(false);
                    const errorMessage = '3DS authentication cancelled';
                    setError(errorMessage);
                    onResult?.({
                      success: false,
                      error: errorMessage,
                    });
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Your card issuer requires additional verification. Please complete the
                authentication in the window below.
              </p>
              <iframe
                id="challenge-iframe"
                name="challenge-iframe"
                ref={challengeIframeRef}
                src="about:blank"
                style={{
                  width: '100%',
                  height: '400px',
                  border: 'none',
                  borderRadius: '4px',
                }}
                title="3DS Challenge"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppCustom3DSFiservCardInput;
